<template>
  <div
    class="main-image-view"
    :class="{'rgb-open': rgbView}" 
    id="main-image-view"
    @wheel.exact.prevent="handleCanvasWheel"
    v-hammer:press="handlePanstart"
    v-hammer:panstart="handlePanstart"
    v-hammer:pan="handlePanmove"
    v-hammer:panend="handlePanend">
    <annotateable-canvas
      :borderColor="borderColor"
      :context="imageBrowserContext"
      :imgIsLoading="imgIsLoading"
      :imgAvailable="imgAvailable"
      :imgNotFound="imgNotFound"
      :imgObject="deepDeeHighlight ? imgMask : displayImgObject"
      :imgSize="displayImgSize"
      :imgOffset="imgOffset"
      :colorSettings="imageColorSettings"
      :viewSize="largeViewSize"
      :viewFit="largeViewFit"
      :transform="transform"
      :isFlipped="isFlipped"
      :isFlipped90="isFlipped90"
      :annotations="annotations"
      :activeAnnotation="activeAnnotation"
      :realLocation="realLocation"
      :isInBounds="isInBounds"
      :annotationByPosition="annotationByPosition"
      :inMagnifyMode="inMagnifyMode"
      :inAddMode="inAddMode"
      :inEditMode="inEditMode"
      :inShapeFillMode="inShapeFillMode"
      :isDragLock="isDragLock"
      :isMagnifying="isMagnifying"
      :magnifier="magnifier"
      :isMask="deepDeeHighlight"
      :isDeepDeeAnnotation="showDeepDeeAnnotation"
      :deepDeeAnnotation="deepDeeAnnotation"
      @mousedownAnnotation="mousedownAnnotation"
      @mousemoveAnnotation="mousemoveAnnotation"
      @mouseupAnnotation="mouseupAnnotation"
      @deleteActiveAnnotation="deleteActiveAnnotation"
      @panBy="$emit('panBy', $event)"
      @updateMagnifier="updateMagnifier"
      @mousedownMagnifier="mousedownMagnifier"
      @mouseupMagnifier="mouseupMagnifier" />
    <div class="main-rgb-container" v-if="rgbView" :style="rgbContainerStyle">
      <rgb-view
        v-for="view in rgbViews"
        :key="view.index"
        :view="view"
        :rgbSize="rgbSize"
        :rgbFit="rgbFit"/>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import annotateableCanvas from "../../canvas/components/AnnotateableCanvas";
import touchMouseEvents from "../../components/mixins/TouchMouseEvents";
import { ANNOTATION_MODES } from "../../config/config";
import rgbView from "./rgbView";
export default {
  name: "mainImageView",
  components: { annotateableCanvas, rgbView },
  mixins: [touchMouseEvents],
  data() {
    return {
      dragStart: null,
      dragStartCenter: null,
      disableCustomEvents: false
    };
  },
  props: [
    "largeViewSize",
    "largeViewFit",
    "rgbSize",
    "rgbFit",
    "transform",
    "realLocation",
    "isInBounds",
    "imgOffset"
  ],
  computed: {
    ...mapGetters([
      "rgbView",
      "rgbViews",
      "isOptos",
      "currentRGBView",
      "imageBrowserContext",
      "imgIsLoading",
      "imgAvailable",
      "imgNotFound",
      "displayImgObject",
      "displayImgSize",
      "imageColorSettings",
      "isFlipped",
      "isFlipped90",
      "annotations",
      "activeAnnotation",
      "annotationByPosition",
      "inMagnifyMode",
      "inAddMode",
      "inEditMode",
      "inShapeFillMode",
      "isDragLock",
      "isMagnifying",
      "magnifier",
      "deepDeeHighlight",
      "imgMask",
      "showDeepDeeAnnotation",
      "deepDeeAnnotation",
      "shapeFillContext"
    ]),
    rgbContainerStyle() {
      let rgbWidth = (this.rgbSize && this.rgbSize.w) || 0;
      if (!this.isOptos) rgbWidth *= 2;
      let w = rgbWidth;
      return { width: w + "px" };
    },
    borderColor() {
      if (!this.currentRGBView) return "white";
      return this.currentRGBView.borderColor;
    }
  },
  methods: {
    ...mapActions(["mousedownAnnotation", "deleteActiveAnnotation", "generateFlatImage"]),
    ...mapMutations(["clearAnnotationMode", "clearActiveAnnotation", "updateMagnifier", "mousedownMagnifier", "mouseupMagnifier"]),
    mousemoveAnnotation({x, y}){
      if (this.activeAnnotation)
        this.activeAnnotation.mousemove(x, y);
    },
    mouseupAnnotation(){
      if (this.inShapeFillMode && this.activeAnnotation)
      {
        this.annotations[this.annotations.indexOf(this.activeAnnotation)].color = this.imageBrowserContext.shapeFillColor;
        this.activeAnnotation.updateColor(this.imageBrowserContext.shapeFillColor);
        this.clearAnnotationMode();
        this.generateFlatImage();
      } 
      if (this.activeAnnotation) this.activeAnnotation.mouseup();
      if (this.imageBrowserContext.annotationMode == ANNOTATION_MODES.ContinuousDraw) this.clearActiveAnnotation();
      if (this.inAddMode && this.imageBrowserContext.annotationMode != ANNOTATION_MODES.ContinuousDraw) this.clearAnnotationMode();
    },
    handleCanvasWheel(e){
      if (e.deltaY > 0) 
        this.$store.dispatch("selectPreviousImage");
      else if (e.deltaY < 0)
        this.$store.dispatch("selectNextImage");
    },
    handleInteractionStart(e) {
      const { x, y } = this.realLocation(e.offsetX, e.offsetY);
      if (this.isDragLock && !this.activeAnnotation) {
        this.setDisableCustomEvents(false);
        this.initiateDragStart(x, y);
      }
    },
    initiateDragStart(x, y) {
      this.dragStart = { x, y };
      this.dragStartCenter = {
        x: this.transform.centerX,
        y: this.transform.centerY
      };
      // logDetailed(
      //   "AnnotateableCanvas",
      //   "initiateDragStart",
      //   "dragStart",
      //   this.dragStart
      // );
    },
    updateDrag(x, y) {
      if (this.isMousedown)
        if (!this.dragStart) this.initiateDragStart(x, y);
        else this.dragTo(x, y);
      else this.clearDragStart();
    },
    dragTo(x, y) {
      let dx = this.dragStart.x - x;
      let dy = this.dragStart.y - y;
      // logDetailed("AnnotateableCanvas", "dragTo", "x", x, "y", y);
      this.$emit("panBy", { dx, dy });
    },
    clearDragStart() {
      this.dragStart = null;
      this.dragStartCenter = null;
    },
    setDisableCustomEvents(val) {
      this.disableCustomEvents = val;
    },
  },
  watch: {
    disableCustomEvents() {
      if (this.disableCustomEvents)
        this.$refs.zoomControl.style.touchAction = "auto";
      else
        this.$refs.zoomControl.style.touchAction = "none";
    },
  }
};
</script>
